/**
 * api-client.ts
 *
 * Thin, typed fetch wrapper for all client-side internal API calls.
 *
 * The method discriminant drives how parameters are sent:
 *   - GET  → params appended to the URL as a query string
 *   - POST / PUT / PATCH / DELETE → params serialised as a JSON body
 *
 * Mutations can also carry `queryParams` when the internal route reads URL
 * parameters in addition to (or instead of) a body (e.g. unfreeze-appointments).
 *
 * Error handling and ClientLogsService calls remain the caller's responsibility.
 */

import { HTTP_METHODS } from "@src/constants/http-methods.constant";
import { getCorrelationId } from "@src/utils/correlation-id";
import { normalizeString } from "@src/utils/string-utils";

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export type GetCallConfig = {
  method: "GET";
  queryParams?: QueryParams;
  /** Extra headers merged over the default accept/content-type headers. */
  extraHeaders?: Record<string, string>;
};

export type MutationCallConfig = {
  method: "POST" | "PUT" | "PATCH" | "DELETE";
  body?: Record<string, unknown>;
  /** URL query params for mutations that send params via URL (e.g. ?userUuid=…). */
  queryParams?: QueryParams;
  /** AbortSignal for requests managed by runManagedRequest. */
  signal?: AbortSignal;
  /** Keep the request alive after page unload (e.g. for client-side logging). */
  keepalive?: boolean;
  /** Extra headers merged over the default accept/content-type headers. */
  extraHeaders?: Record<string, string>;
};

export type ApiCallConfig = {
  /** Internal API endpoint path, e.g. INTERNAL_API_ENDPOINTS.ADDRESS_SEARCH. */
  endpoint: string;
} & (GetCallConfig | MutationCallConfig);

function buildUrl(endpoint: string, queryParams?: QueryParams): string {
  if (!queryParams) return endpoint;

  const entries = Object.entries(queryParams).filter(([, v]) => v !== undefined && v !== null) as [
    string,
    string | number | boolean,
  ][];

  if (entries.length === 0) return endpoint;

  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return `${endpoint}?${qs}`;
}

/**
 * Makes a typed fetch call to an internal API endpoint.
 *
 * Returns `{ data, response }` so callers can inspect both the parsed body
 * and the raw Response (status, headers, ok).
 *
 * JSON parse failures are swallowed and `data` is set to `null`; this mirrors
 * the server-side `safeJsonParse` behaviour in `api-route-utils.ts`.
 */
export async function callApi(
  config: ApiCallConfig,
): Promise<{ data: unknown; response: Response }> {
  const { endpoint } = config;

  const url = buildUrl(endpoint, config.queryParams);

  const isGet = config.method === HTTP_METHODS.GET;

  const fetchOptions: RequestInit = {
    method: config.method,
    headers: {
      accept: "application/json",
      ...(!isGet && { "Content-Type": "application/json" }),
      "x-correlation-id": getCorrelationId(),
      ...("extraHeaders" in config && config.extraHeaders),
    },
  };

  if (!isGet) {
    if ("body" in config && config.body !== undefined) {
      fetchOptions.body = JSON.stringify(config.body);
    }

    if ("signal" in config && config.signal) {
      fetchOptions.signal = config.signal;
    }

    if ("keepalive" in config && config.keepalive) {
      fetchOptions.keepalive = true;
    }
  }

  const response = await fetch(url, fetchOptions);

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    data = null;
  }

  return { data, response };
}

/**
 * Extracts a human-readable error string from an API response payload.
 * Checks `payload.error`, then `payload.message` (string or string[]),
 * and falls back to the HTTP status line.
 */
export function extractApiErrorMessage(payload: unknown, response: Response): string {
  if (typeof payload === "object" && payload !== null) {
    const error = (payload as { error?: unknown }).error;
    if (normalizeString(error)) {
      return error as string;
    }

    const message = (payload as { message?: unknown }).message;
    if (normalizeString(message)) {
      return message as string;
    }

    if (Array.isArray(message) && message.length > 0) {
      return message.join(", ");
    }
  }

  return `HTTP ${response.status}: ${response.statusText}`;
}

/**
 * Generic type guard that narrows an unknown API payload to any type T
 * that carries a boolean `success` field — the standard shape returned by
 * all internal service responses.
 *
 * Replaces the per-service `isNormalized*Response` helpers that were
 * identical in structure across every service file.
 */
export function hasSuccessFlag<T extends { success: boolean }>(payload: unknown): payload is T {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "success" in payload &&
    typeof (payload as { success: unknown }).success === "boolean"
  );
}
